import { createContext } from "react";
import { ChaptersScoreWithMetadata, Score } from "src/types";
import { ActionUrls } from "./provider";
import { ScoreState } from "../EntityBasedScore/useEntityBasedScoreParams";
import { Post } from "src/api/client-api/post/postSchema";
import { ScoreVariation } from "../EntityBasedScore/ScoreService";
import { ActionsType } from "src/api/sure-api/actions/useGetOneAction";
import { SavingStatus } from "src/components/Loader/useSavingStatus";
import { OneActionPlan } from "src/api/client-api/ActionPlan/ActionPlan/useGetOneActionPlan";
import { ActionPlanData, APScore } from "./useActionPlanData";
import {
    ActionPlanAction,
    PropertiesTree,
} from "src/api/client-api/ActionPlan/ActionPlanAction/useGetOneActionPlanAction";
import { ActionsByCsrThemes } from "src/api/sure-api/actions/useGetAllActions";
import { ActionScores, PostScoresToCompare } from "./useActionScore";
import { Property } from "../Property/Builder/PropertyBuilder";
import { ActionPlanModes } from "src/api/client-api/appParams/useApplicationParameters";
import { t } from "i18next";

export type ActionPlanConfig = {
    id: number;
    label: string;
};
export type ActionPlanConfigMode = Record<ActionPlanModes, ActionPlanConfig>;
export const ActionPlanConfiguration: ActionPlanConfigMode = {
    simplified: {
        id: 0,
        label: t("action-plan.mode.simplified"),
    },
    advanced: {
        id: 1,
        label: t("action-plan.mode.advanced"),
    },
};

type ActionID = number;
export type ActionData = {
    totalScore: number | null;
    data: Property[];
};
export type ActionsDataMapping = Partial<Record<ActionID, ActionData>>;

export type ActionsCsr = {
    totalScore: number | null;
    whitelistScore?: number | null;
    data: ActionsDataMapping;
};
export type ActionsCsrMapping = Partial<Record<string, ActionsCsr>>;

export type ScoreWithVariation = {
    score: Score | undefined;
    variation: ChaptersScoreWithMetadata | undefined;
};

export type PersistedOptions = {
    readonly withoutPlanName: string;
    readonly withPlanName: string;
    readonly comparedToWithoutPlan: string;
};
export type ActionPlanMetadata = {
    actionPlanIRI: IRI | undefined;
    actionPlanID: string | undefined;
    startingPostId: number | undefined;
    targetPostIdWithoutActionPlan: number | undefined;
    targetPostIdWithActionPlan: number | undefined;
    firstPostIdWithActionPlan: number | undefined;
    isGrowthDisabled: boolean;
};

export type ElligibleStartingPost = Pick<Post, "scores"> & {
    id: number;
    isLocked: boolean;
    campaign: {
        id: number;
        label: string;
        startDate: string;
        endDate: string;
    };
};

export type PostActionData = {
    allPosts: APScore[];
    foundStartPost: APScore | undefined;
    foundEndPost: APScore | undefined;
};

export type ActionsContextInitialValues = {
    actionPlanStats: {
        total: number;
        engagedActions: number;
    };
    actionPlanMetadata: ActionPlanMetadata;
    actionPlanData: ActionPlanData[];
    selectedActionPlan: ActionPlanData | undefined;
    selectPostsByActionPlanAction: (plan: ActionPlanData, action: ActionPlanAction) => PostScoresToCompare;
    selectPostsByCampaigns: (
        plan: ActionPlanData | undefined,
        startingCampaign: number | undefined,
        targetCampaign: number | undefined,
        includeStart?: boolean,
    ) => PostScoresToCompare;

    getActionScoresByCsrTheme: (
        postScores: PostScoresToCompare,
        whitelistActionIds?: number[],
        propertiesTree?: PropertiesTree,
    ) => ActionScores | undefined;

    getPropertiesByAction: (
        postScores: PostScoresToCompare,
        actionId: number,
        propertiesTree?: PropertiesTree,
    ) => { start: ActionData; end: ActionData } | undefined;

    actions: ActionsType[] | undefined;
    actionPlan: OneActionPlan | null;
    actionScores: ActionScores | undefined;
    elligibleStartingPosts: ElligibleStartingPost[] | undefined;
    isFetched: boolean;
    isRefetching: boolean;
    savingStatus: SavingStatus;
    actionPlanSavingStatus: SavingStatus;
    actionsByCsrTheme: ActionsByCsrThemes;
    allScoreWithVariations: ScoreVariation[];
    selectedScore: ScoreVariation;
    scoresEnabled: Score[];
    scoreOptions: ScoreState;
    urls: ActionUrls;
    setOpenInitForm: (isOpen: boolean) => void;
    isEditMode: boolean;
    toggleEditMode: () => void;
    persistedOptions: PersistedOptions;
    areActionsLoading: boolean;
};
export const ActionsContext = createContext<ActionsContextInitialValues | null>(null);
